import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, ArrowRight, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Tables } from "@/integrations/supabase/types";

type SongOrder = Tables<"song_orders">;

const eventTypeLabels: Record<string, string> = {
  wedding: "Wedding", birthday: "Birthday", anniversary: "Anniversary",
  graduation: "Graduation", memorial: "Memorial", proposal: "Proposal",
  baby_shower: "Baby Shower", retirement: "Retirement", other: "Other",
  quinceanera: "Quinceañera", new_baby: "New Baby", adoption: "Adoption",
  promotion: "Promotion", personal_milestone: "Personal Milestone", just_because: "Just Because",
};

interface AbandonedCartBannerProps {
  order: SongOrder;
  onDismiss: (orderId: string) => void;
}

const AbandonedCartBanner = ({ order, onDismiss }: AbandonedCartBannerProps) => {
  const navigate = useNavigate();
  const [resuming, setResuming] = useState(false);

  const handleResume = async () => {
    setResuming(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          planType: order.plan_type,
          rushDelivery: order.rush_delivery || false,
          orderId: order.id,
          successUrl: `${window.location.origin}/checkout/success?order_id=${order.id}&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/dashboard`,
        },
      });

      if (error) throw error;
      if (data?.error) {
        toast.error(data.error);
        setResuming(false);
        return;
      }
      if (data?.url) {
        window.open(data.url, "_blank");
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      console.error("Resume checkout error:", err);
      toast.error("Could not resume checkout. Please try again.");
      setResuming(false);
    }
  };

  const occasionLabel = eventTypeLabels[order.event_type] || order.event_type;
  const planLabel = order.plan_type === "premium" ? "Premium" : "Essentials";

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-primary/5 border border-primary/20 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
    >
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
        <ShoppingCart className="w-5 h-5 text-primary" aria-hidden="true" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-heading text-sm font-semibold text-foreground">
          Complete your order
        </h3>
        <p className="text-sm text-muted-foreground">
          {occasionLabel} song for {order.recipient_names} · {planLabel} Plan
          {order.rush_delivery && " · Rush Delivery"}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          variant="hero"
          size="sm"
          onClick={handleResume}
          disabled={resuming}
        >
          {resuming ? (
            <Loader2 className="w-4 h-4 animate-spin mr-1" aria-hidden="true" />
          ) : (
            <ArrowRight className="w-4 h-4 mr-1" aria-hidden="true" />
          )}
          Pay Now
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDismiss(order.id)}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="w-4 h-4" aria-hidden="true" /><span className="sr-only">Dismiss</span>
        </Button>
      </div>
    </motion.div>
  );
};

export default AbandonedCartBanner;
